import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Pop-up que aparece cuando la persona elige Windows. Se muestra una sola vez por cuenta
// (cuando user.decision_so todavía no tiene valor). Obliga a elegir una de las tres opciones.
export default function AvisoWindows({ open, onResolver }) {
  const { refreshMe } = useAuth();
  const [guardando, setGuardando] = useState(false);
  const [cierre, setCierre] = useState(null); // { decision, mensaje }

  useEffect(() => {
    if (open) { setCierre(null); setGuardando(false); }
  }, [open]);

  const decidir = async (decision) => {
    if (guardando) return;
    setGuardando(true);
    const datos = { decision_so: decision };
    if (decision === "uso_mac") datos.sistema_operativo = "mac";
    try { await api.post("/preparar", { datos }); } catch (e) { /* no bloqueamos el flujo */ }
    try { refreshMe && refreshMe(); } catch (e) { /* noop */ }
    setGuardando(false);
    if (decision === "uso_mac") {
      onResolver && onResolver("uso_mac");
    } else if (decision === "busco_mac") {
      setCierre({
        decision,
        mensaje: "Perfecto. Mientras tanto seguimos con Windows y lo dejamos bien configurado. Cuando la tengas, cambiás el sistema desde tu perfil y listo.",
      });
    } else {
      setCierre({
        decision,
        mensaje: "Tranquilo, está bien. Funciona. Solo que va a ser un poco más lento y va a usar tu pantalla mientras trabaja. Lo configuramos bien para que ande lo mejor posible.",
      });
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="aviso-windows-overlay"
        >
          <motion.div
            className="rounded-2xl w-full max-w-lg p-7"
            style={{ background: "#141414", border: "1px solid rgba(201,168,76,0.35)" }}
            initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
            data-testid="aviso-windows-modal"
          >
            <h2 className="titulo text-crema mb-5" style={{ fontSize: "26px", lineHeight: 1.15 }} data-testid="aviso-windows-titulo">
              Antes de seguir, una cosa sobre Windows.
            </h2>
            <div className="space-y-3 mb-6 text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }}>
              <p>Las herramientas de agentes de IA son tan nuevas que se optimizan primero para Mac. En Mac funcionan mucho mejor: el agente trabaja por atrás mientras vos seguís con lo tuyo, y va más ágil.</p>
              <p>En Windows anda, pero te toma la pantalla mientras trabaja y va más lento.</p>
              <p>Por eso lo que recomendamos es usar una Mac para esto, aunque sea una que tengas dando vueltas. La experiencia con los agentes es otra.</p>
            </div>

            {cierre ? (
              <div>
                <div className="rounded-lg p-4 mb-5" style={{ border: "1px solid rgba(201,168,76,0.3)", background: "rgba(201,168,76,0.05)" }}>
                  <p className="text-crema" style={{ fontSize: "15px", lineHeight: 1.6 }} data-testid="aviso-windows-mensaje">{cierre.mensaje}</p>
                </div>
                <button className="btn-dorado w-full !py-3" onClick={() => onResolver && onResolver(cierre.decision)} data-testid="aviso-windows-seguir">
                  SEGUIR
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                <button className="btn-dorado w-full !py-3" disabled={guardando} onClick={() => decidir("uso_mac")} data-testid="aviso-windows-uso-mac">
                  VOY A USAR UNA MAC
                </button>
                <button className="btn-fantasma w-full !py-3" disabled={guardando} onClick={() => decidir("busco_mac")} data-testid="aviso-windows-busco-mac">
                  VOY A CONSEGUIR UNA MAC
                </button>
                <button className="btn-fantasma w-full !py-3" disabled={guardando} onClick={() => decidir("windows")} data-testid="aviso-windows-windows">
                  NO ME QUEDA OTRA, USO WINDOWS
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
